import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../data/client";

/** The staff Problems log (owner 2026-11-02, design "Bell & problems log" D2). Full Super Admin only; the server answers 403 to anyone else. */
const N = "/api/auth/next/staff/problems";
const k = (...p: unknown[]) => ["staff", "problems", ...p];

export const PROBLEM_KINDS = ["report", "optimization", "import", "geocoder", "mail", "job", "payment", "other"] as const;
export type ProblemKind = (typeof PROBLEM_KINDS)[number];
export type ProblemStatus = "open" | "handled" | "all";
export const PROBLEM_SINCE = ["today", "7", "30", "90"] as const;
export type ProblemSince = (typeof PROBLEM_SINCE)[number];

export type ProblemFilters = { status: ProblemStatus; kind: ProblemKind | null; account: string; since: ProblemSince };
export const DEFAULT_FILTERS: ProblemFilters = { status: "open", kind: null, account: "", since: "7" };

export type ProblemRow = {
  id: number;
  kind: ProblemKind;
  /** A key of next.console.problems.explanation (the plain sentence). */
  explanation: string;
  place: string;
  workspace_id: number | null;
  /** Read live; null when the account is gone. */
  workspace_name: string | null;
  user_id: number | null;
  /** Codes and ids for support: flat values, `ids` a map of numbers. */
  details: Record<string, string | number | boolean | Record<string, number>>;
  repeats: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
  open: boolean;
  handled_at: string | null;
  handled_by: string | null;
};
type Page = {
  data: ProblemRow[];
  meta: { page: number; last_page: number; total: number; open_count: number; handled_recent: number; ever: boolean; seen_before: string | null };
};

const params = (f: ProblemFilters, page: number) => ({
  status: f.status, since: f.since, page, ...(f.kind ? { kind: f.kind } : {}), ...(f.account.trim() ? { account: f.account.trim() } : {}),
});

export const useProblems = (f: ProblemFilters) => useInfiniteQuery({
  queryKey: k("list", f),
  queryFn: ({ pageParam }) => api.get<Page>(N, params(f, pageParam)),
  initialPageParam: 1,
  getNextPageParam: (last) => (last.meta.page < last.meta.last_page ? last.meta.page + 1 : undefined),
  placeholderData: keepPreviousData,
  retry: false,
});

/** "Show N" on the phone's filter sheet: how many the draft filters would show. */
export const useProblemCount = (f: ProblemFilters, enabled: boolean) => useQuery({
  queryKey: k("count", f),
  queryFn: async () => (await api.get<Page>(N, params(f, 1))).meta.total,
  enabled,
  placeholderData: keepPreviousData,
  retry: false,
});

/** The menu's red number (P2): open problems that came in since this person last opened the page. */
export const useProblemsNewCount = (enabled: boolean) => useQuery({
  queryKey: k("new-count"),
  queryFn: async () => (await api.get<{ data: { count: number } }>(`${N}/new-count`)).data.count,
  enabled,
  refetchInterval: enabled ? 60_000 : false,
  retry: false,
});

/** The page was opened: the count starts again from now. Answers when the person last opened it (for the blue edges). */
export const useMarkProblemsSeen = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post<{ data: { seen_before: string | null } }>(`${N}/seen`)).data.seen_before,
    onSuccess: () => { client.setQueryData(k("new-count"), 0); },
  });
};

export const useMarkProblemHandled = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => { await api.post(`${N}/${id}/handled`); },
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: k("list") }), client.invalidateQueries({ queryKey: k("count") }), client.invalidateQueries({ queryKey: k("new-count") })]),
  });
};

/** The technical details as plain "key: value" lines (for the dark box and Copy). */
export const technicalText = (p: ProblemRow): string => {
  const lines: string[] = [`problem: #${p.id}`, `kind:    ${p.kind}`, `place:   ${p.place}`];
  if (p.workspace_id) lines.push(`account: #${p.workspace_id}`);
  if (p.user_id) lines.push(`person:  #${p.user_id}`);
  for (const [key, value] of Object.entries(p.details ?? {})) {
    const v = typeof value === "object" && value !== null ? Object.entries(value).map(([a, b]) => `${a} #${b}`).join("   ") : String(value);
    lines.push(`${(key + ":").padEnd(8)} ${v}`);
  }
  return lines.join("\n");
};
